import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Button } from '../components/ui/button';
import { HelpCircle, X, ChevronLeft, ChevronRight, EyeOff } from 'lucide-react';

const STORAGE_KEY = 'resawork-guide-completed';
const GLOBAL_DISABLE_KEY = 'resawork-guides-disabled-globally';
const PRIMARY = '#84CC16'; // lime-500
const PADDING = 8; // spotlight padding around the target

// Returns true if the user has globally opted-out of all guide tours
function areGuidesGloballyDisabled() {
  try { return localStorage.getItem(GLOBAL_DISABLE_KEY) === '1'; } catch { return false; }
}

// ===== Default tour: Back-office company =====
const COMPANY_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: 'Bienvenue sur ResaWork 🎉',
    content: 'Découvrons votre back-office en 8 étapes (≈ 30 secondes). Vous pourrez relancer ce guide à tout moment via la barre latérale.',
  },
  {
    target: '[data-testid="nav--company"]',
    placement: 'right',
    title: '📊 Tableau de bord',
    content: 'Votre vue d\'ensemble : KPI clés (clients, créneaux, demandes), chiffre d\'affaires, et accès rapide aux actions.',
  },
  {
    target: '[data-testid="nav--company-planning"]',
    placement: 'right',
    title: '📅 Planning',
    content: 'Visualisez et gérez vos rendez-vous & interventions. Drag & drop pour reprogrammer, export PDF par membre.',
  },
  {
    target: '[data-testid="nav--company-collaborators"]',
    placement: 'right',
    title: '👥 Collaborateurs',
    content: 'Votre équipe : techniciens terrain et commerciaux (★), avec leurs horaires, congés et avatars.',
  },
  {
    target: '[data-testid="nav--company-clients"]',
    placement: 'right',
    title: '🤝 Clients',
    content: 'Toute votre base client : historique de réservations, contacts, fiches détaillées.',
  },
  {
    target: '[data-testid="group-toggle-activity"]',
    placement: 'right',
    title: '✅ Activité',
    content: 'Centralisez les Demandes, prestations terminées et avis clients. L\'algorithme suggère automatiquement le meilleur collaborateur.',
  },
  {
    target: '[data-testid="group-toggle-widgets"]',
    placement: 'right',
    title: '🔗 Widgets',
    content: 'Créez les widgets de réservation à intégrer sur votre site web. RDV, devis, ponctuel, abonnement.',
  },
  {
    target: '[data-testid="quick-action-fab"]',
    placement: 'left',
    title: '➕ Action rapide',
    content: 'Ce bouton flottant vous permet de créer rapidement un client, une réservation ou une intervention depuis n\'importe où.',
  },
  {
    target: '[data-testid="open-guide-tour-btn"]',
    placement: 'right',
    title: 'Vous êtes prêt ! 🚀',
    content: 'Relancez ce guide à tout moment via ce bouton. Bonne utilisation de ResaWork !',
  },
];

// Compute the modal position next to the target rect, given the desired placement
function computeModalPosition(rect, placement, modalSize) {
  const { width: mw, height: mh } = modalSize;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 16;

  if (!rect || placement === 'center') {
    return {
      left: Math.max(margin, (vw - mw) / 2),
      top: Math.max(margin, (vh - mh) / 2),
    };
  }

  let left, top;
  switch (placement) {
    case 'right':
      left = rect.x + rect.width + margin;
      top = rect.y + rect.height / 2 - mh / 2;
      break;
    case 'left':
      left = rect.x - mw - margin;
      top = rect.y + rect.height / 2 - mh / 2;
      break;
    case 'top':
      left = rect.x + rect.width / 2 - mw / 2;
      top = rect.y - mh - margin;
      break;
    case 'bottom':
    default:
      left = rect.x + rect.width / 2 - mw / 2;
      top = rect.y + rect.height + margin;
      break;
  }

  // Clamp to viewport
  left = Math.max(margin, Math.min(left, vw - mw - margin));
  top = Math.max(margin, Math.min(top, vh - mh - margin));
  return { left, top };
}

function TourOverlay({ steps, onClose, onGlobalDisable }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [modalPos, setModalPos] = useState({ left: 0, top: 0, opacity: 0 });
  const modalRef = useRef(null);
  const step = steps[stepIdx];
  const isCenter = !step || step.target === 'body' || step.placement === 'center';

  // Resolve target rectangle whenever step changes (or window resizes)
  useLayoutEffect(() => {
    if (!step) return;
    if (isCenter) {
      setRect(null);
      return;
    }
    const find = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        setRect(null);
        return;
      }
      // Make sure the target is in view
      try { el.scrollIntoView({ behavior: 'instant', block: 'nearest' }); } catch {}
      const r = el.getBoundingClientRect();
      setRect({ x: r.x, y: r.y, width: r.width, height: r.height });
    };
    find();
    const t = setTimeout(find, 60); // re-check after small delay
    const onResize = () => find();
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(t); window.removeEventListener('resize', onResize); };
  }, [step, isCenter]);

  // Position modal once rect is known + modalRef is mounted
  useLayoutEffect(() => {
    if (!modalRef.current) return;
    const m = modalRef.current.getBoundingClientRect();
    const pos = computeModalPosition(rect, step?.placement || 'center', { width: m.width, height: m.height });
    setModalPos({ ...pos, opacity: 1 });
  }, [rect, step]);

  const next = () => {
    if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
    else handleFinish();
  };
  const prev = () => stepIdx > 0 && setStepIdx(stepIdx - 1);
  const skip = () => handleFinish();

  const handleFinish = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    onClose();
  }, [onClose]);

  if (!step) return null;

  // 4 dark panels around the spotlight rect (or full overlay if no rect)
  const overlayBase = {
    position: 'fixed',
    background: 'rgba(15, 23, 42, 0.65)',
    zIndex: 9998,
    transition: 'all 280ms cubic-bezier(0.4,0,0.2,1)',
  };

  const overlay = !rect
    ? <div style={{ ...overlayBase, inset: 0 }} />
    : (
      <>
        <div style={{ ...overlayBase, top: 0, left: 0, right: 0, height: Math.max(0, rect.y - PADDING) }} />
        <div style={{ ...overlayBase, top: rect.y + rect.height + PADDING, left: 0, right: 0, bottom: 0 }} />
        <div style={{ ...overlayBase, top: rect.y - PADDING, left: 0, width: Math.max(0, rect.x - PADDING), height: rect.height + PADDING * 2 }} />
        <div style={{ ...overlayBase, top: rect.y - PADDING, left: rect.x + rect.width + PADDING, right: 0, height: rect.height + PADDING * 2 }} />
      </>
    );

  // Bright glowing border around target
  const spotlight = rect && (
    <div
      style={{
        position: 'fixed',
        top: rect.y - PADDING,
        left: rect.x - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
        border: `3px solid ${PRIMARY}`,
        borderRadius: 14,
        boxShadow: `0 0 0 4px rgba(132, 204, 22, 0.25), 0 0 32px 6px rgba(132, 204, 22, 0.55), 0 0 60px 8px rgba(132, 204, 22, 0.25)`,
        zIndex: 9999,
        pointerEvents: 'none',
        transition: 'all 280ms cubic-bezier(0.4,0,0.2,1)',
        animation: 'resawork-pulse 1.6s ease-in-out infinite',
      }}
    />
  );

  return (
    <>
      <style>{`
        @keyframes resawork-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(132, 204, 22, 0.25), 0 0 32px 6px rgba(132, 204, 22, 0.55), 0 0 60px 8px rgba(132, 204, 22, 0.25); }
          50%      { box-shadow: 0 0 0 6px rgba(132, 204, 22, 0.45), 0 0 42px 12px rgba(132, 204, 22, 0.75), 0 0 80px 14px rgba(132, 204, 22, 0.4); }
        }
      `}</style>

      {overlay}
      {spotlight}

      {/* Modal */}
      <div
        ref={modalRef}
        data-testid="guide-tour-modal"
        style={{
          position: 'fixed',
          top: modalPos.top,
          left: modalPos.left,
          opacity: modalPos.opacity,
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          background: 'white',
          borderRadius: 14,
          padding: 22,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0,0,0,0.05)',
          zIndex: 10000,
          transition: 'opacity 200ms ease, top 280ms cubic-bezier(0.4,0,0.2,1), left 280ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <button
          type="button"
          onClick={skip}
          aria-label="Fermer"
          className="absolute top-3 right-3 w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>

        <div className="flex items-center gap-2 text-xs font-medium text-primary uppercase tracking-wider mb-2">
          <span>Étape {stepIdx + 1}/{steps.length}</span>
        </div>
        <h3 className="text-lg font-bold text-slate-900 leading-tight">{step.title}</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{step.content}</p>

        {/* Progress dots */}
        <div className="flex gap-1 mt-4">
          {steps.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === stepIdx ? 24 : 8,
                background: i <= stepIdx ? PRIMARY : '#e2e8f0',
              }}
            />
          ))}
        </div>

        <div className="flex items-center justify-between mt-5">
          <div className="flex flex-col gap-1 items-start">
            <button
              type="button"
              onClick={skip}
              className="text-xs text-slate-400 hover:text-slate-600"
              data-testid="guide-skip"
            >
              Passer
            </button>
            <button
              type="button"
              onClick={() => {
                try { localStorage.setItem(GLOBAL_DISABLE_KEY, '1'); } catch {}
                if (onGlobalDisable) onGlobalDisable();
                handleFinish();
              }}
              className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-rose-600"
              data-testid="guide-global-disable"
            >
              <EyeOff className="w-3 h-3" />
              Ne plus afficher
            </button>
          </div>
          <div className="flex items-center gap-2">
            {stepIdx > 0 && (
              <Button variant="outline" size="sm" onClick={prev} data-testid="guide-prev">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                Retour
              </Button>
            )}
            <Button
              size="sm"
              onClick={next}
              data-testid="guide-next"
              style={{ background: PRIMARY, color: 'white' }}
            >
              {stepIdx === steps.length - 1 ? 'Terminer' : (
                <>Suivant<ChevronRight className="w-3.5 h-3.5 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// Module-level counter — incremented while a global `GuideTour` is mounted
// on the page. Mini-guides use it to detect coexistence and defer their
// auto-start to avoid stacked overlays (see fix in v5.16.36).
let _globalGuidesMounted = 0;

export default function GuideTour({ steps = COMPANY_STEPS, autoStartForNewUsers = true, label = 'Lancer le guide', testId = 'open-guide-tour-btn' }) {
  const [run, setRun] = useState(false);

  useEffect(() => {
    _globalGuidesMounted += 1;
    return () => { _globalGuidesMounted = Math.max(0, _globalGuidesMounted - 1); };
  }, []);

  useEffect(() => {
    if (!autoStartForNewUsers) return;
    if (areGuidesGloballyDisabled()) return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setRun(true), 1200);
        return () => clearTimeout(t);
      }
    } catch {}
  }, [autoStartForNewUsers]);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setRun(true)}
        data-testid={testId}
        className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/[0.06]"
      >
        <HelpCircle className="w-4 h-4 mr-2" />
        {label}
      </Button>
      {run && <TourOverlay steps={steps} onClose={() => setRun(false)} />}
    </>
  );
}

// Reusable mini-tour for the Widgets page (3 steps)
export const WIDGETS_MINI_STEPS = [
  {
    target: '[data-testid="widgets-page-title"]',
    placement: 'bottom',
    title: '🔗 Vos widgets',
    content: 'Chaque widget est un module de réservation que vos clients utilisent depuis votre site.',
  },
  {
    target: '[data-testid="create-widget-btn"]',
    placement: 'left',
    title: '➕ Créer un widget',
    content: '4 types disponibles : RDV commercial (visio/physique), Devis, Réservation ponctuelle (à la demande), Abonnement (récurrent).',
  },
  {
    target: '[data-testid="widget-list"]',
    placement: 'top',
    title: '📋 Code à intégrer',
    content: 'Pour chaque widget, copiez le snippet HTML/JS et collez-le sur votre site web. Le widget se met à jour automatiquement.',
  },
];

const WIDGETS_STORAGE_KEY = 'resawork-widgets-guide-completed';
export function WidgetsGuideTour({ autoStart = true }) {
  return <ContextualGuide steps={WIDGETS_MINI_STEPS} storageKey={WIDGETS_STORAGE_KEY} autoStart={autoStart} />;
}

// ===== Reusable contextual mini-guide =====
// Renders both the auto-start logic AND a small "Revoir le guide" button
export function ContextualGuide({ steps, storageKey, autoStart = true, buttonLabel = 'Revoir le guide', buttonClassName = '' }) {
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (!autoStart) return;
    if (areGuidesGloballyDisabled()) return;
    try {
      // Defer only when a global `GuideTour` is actually mounted on the
      // same page — otherwise stacked overlays would intercept pointer
      // events (see v5.16.36 stacked-tours fix). On pages without a global
      // tour (e.g. client dashboard) the mini-guide auto-starts as before.
      const globalPending = _globalGuidesMounted > 0 && !localStorage.getItem(STORAGE_KEY);
      if (globalPending) return;
      if (!localStorage.getItem(storageKey)) {
        const t = setTimeout(() => setRun(true), 600);
        return () => clearTimeout(t);
      }
    } catch {}
  }, [autoStart, storageKey]);

  const handleClose = () => {
    try { localStorage.setItem(storageKey, '1'); } catch {}
    setRun(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setRun(true)}
        data-testid={`replay-${storageKey}`}
        className={buttonClassName || 'inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors'}
      >
        <HelpCircle className="w-3.5 h-3.5" />
        {buttonLabel}
      </button>
      {run && <TourOverlay steps={steps} onClose={handleClose} />}
    </>
  );
}

// ===== Planning mini-guide =====
export const PLANNING_MINI_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: '📅 Bienvenue dans le Planning',
    content: 'Découvrez les fonctionnalités clés de votre planning en 4 étapes.',
  },
  {
    target: '[data-testid="export-pdf-btn"]',
    placement: 'bottom',
    title: '📄 Export PDF',
    content: 'Exportez le planning global ou une page par membre — pratique pour vos équipes terrain qui partent en intervention.',
  },
  {
    target: 'body',
    placement: 'center',
    title: '🎯 Réserver depuis le planning',
    content: 'Cliquez sur le bouton "Réserver" en haut à droite pour créer une réservation directement dans la grille horaire.',
  },
  {
    target: 'body',
    placement: 'center',
    title: '✨ Gestion par membre',
    content: 'Chaque ligne représente un collaborateur avec ses créneaux disponibles, congés et réservations. Les commerciaux apparaissent en tête de liste.',
  },
];

// ===== Collaborators mini-guide =====
export const COLLABORATORS_MINI_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: '👥 Vos Collaborateurs',
    content: 'Découvrez la gestion d\'équipe en 3 étapes rapides.',
  },
  {
    target: '[data-testid="add-collaborator-btn"]',
    placement: 'left',
    title: '➕ Ajouter un membre',
    content: 'Créez un compte collaborateur (technicien terrain) ou commercial. Chaque rôle a ses spécificités : les commerciaux gèrent les RDV courts, les collaborateurs gèrent les interventions terrain.',
  },
  {
    target: 'body',
    placement: 'center',
    title: '⚙️ Configuration avancée',
    content: 'Pour chaque membre : avatar, rôle (commercial ★ vs collaborateur), horaires hebdomadaires, congés, et catégories de services qu\'ils peuvent traiter.',
  },
];

// ===== Clients mini-guide =====
export const CLIENTS_MINI_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: '🤝 Votre base Clients',
    content: 'Centralisez toutes les infos de vos clients en 3 étapes.',
  },
  {
    target: '[data-testid="create-client-btn"]',
    placement: 'left',
    title: '➕ Ajouter un client',
    content: 'Créez manuellement une fiche client (nom, contact, adresse) ou laissez-les s\'inscrire eux-mêmes via votre espace client.',
  },
  {
    target: 'body',
    placement: 'center',
    title: '📋 Fiche détaillée',
    content: 'Cliquez sur un client pour voir son historique : RDV passés, interventions, abonnements actifs, factures, contacts. C\'est votre mini-CRM intégré.',
  },
];

// ===== Client Dashboard (espace client) mini-guide =====
export const CLIENT_DASHBOARD_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: 'Bienvenue sur votre espace 🌿',
    content: 'Découvrez votre espace personnel en 6 étapes rapides. Vous pourrez relancer ce guide à tout moment depuis la barre latérale.',
  },
  {
    target: '[data-testid="client-nav-dashboard"]',
    placement: 'right',
    title: '🏠 Dashboard',
    content: 'Votre vue d\'ensemble : prochaines interventions, statut d\'abonnement, et accès rapide pour réserver ou laisser un avis.',
  },
  {
    target: '[data-testid="client-nav-subscription"]',
    placement: 'right',
    title: '🔄 Mon Abonnement',
    content: 'Visualisez et gérez votre abonnement récurrent : passages programmés, prochaines interventions, options.',
  },
  {
    target: '[data-testid="client-nav-schedule"]',
    placement: 'right',
    title: '📅 Planifier',
    content: 'Planifiez une nouvelle intervention ponctuelle en choisissant la date et le créneau qui vous arrangent.',
  },
  {
    target: '[data-testid="client-nav-bookings"]',
    placement: 'right',
    title: '📋 Mes Réservations',
    content: 'Toutes vos réservations à venir et passées, avec la possibilité de les modifier ou de les annuler.',
  },
  {
    target: '[data-testid="client-nav-support"]',
    placement: 'right',
    title: '💬 Support',
    content: 'Une question ? Un souci avec une intervention ? Contactez directement votre prestataire depuis cet onglet.',
  },
];

const PLANNING_KEY = 'resawork-planning-guide-completed';
export function PlanningGuideTour({ autoStart = true }) {
  return <ContextualGuide steps={PLANNING_MINI_STEPS} storageKey={PLANNING_KEY} autoStart={autoStart} />;
}

const COLLABS_KEY = 'resawork-collabs-guide-completed';
export function CollaboratorsGuideTour({ autoStart = true }) {
  return <ContextualGuide steps={COLLABORATORS_MINI_STEPS} storageKey={COLLABS_KEY} autoStart={autoStart} />;
}

const CLIENTS_KEY = 'resawork-clients-guide-completed';
export function ClientsGuideTour({ autoStart = true }) {
  return <ContextualGuide steps={CLIENTS_MINI_STEPS} storageKey={CLIENTS_KEY} autoStart={autoStart} />;
}

const CLIENT_DASHBOARD_KEY = 'resawork-client-dashboard-guide-completed';
export function ClientDashboardGuideTour({ autoStart = true, buttonClassName = '' }) {
  return (
    <ContextualGuide
      steps={CLIENT_DASHBOARD_STEPS}
      storageKey={CLIENT_DASHBOARD_KEY}
      autoStart={autoStart}
      buttonClassName={buttonClassName}
    />
  );
}
